(() => {
  class VenroProductControls extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.abort = new AbortController();
      this.root = this.closest('venro-customizer,venro-coaster-customizer,venro-wheel-customizer,venro-holiday-customizer');
      const signal = this.abort.signal;
      customElements.whenDefined(this.root.localName).then(() => {
        if (signal.aborted) return;
        this.form = this.root.querySelector('form');
        this.floating = this.querySelector('[data-floating-preview]');
        this.root.floating = this.floating;
        this.canvas = this.querySelector('canvas');
        const on = (el, type, fn, capture = false) => el?.addEventListener(type, fn, { signal, capture });
        const update = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.updateFloating());
        };
        this.groups = [...this.form.querySelectorAll('[data-customize-group]')];
        this.labels = JSON.parse(this.querySelector('[data-summary-labels]').textContent);
        this.summaryKey = null;
        this.groups.forEach(group => {
          const summary = group.querySelector('summary'), title = document.createElement('span');
          title.className = 'vn-customize-heading';
          title.textContent = summary.querySelector('.vn-customize-heading')?.textContent || summary.textContent;
          const caption = document.createElement('span');
          caption.className = 'vn-customize-caption';
          summary.replaceChildren(title, caption);
        });
        this.groups.forEach(group => on(group, 'toggle', () => {
          if (group.open) this.openGroup(group);
          update();
          requestAnimationFrame(() => this.revealFocusedField());
        }));
        on(this.form, 'invalid', event => this.openGroup(event.target.closest('[data-customize-group]')), true);
        on(this.root, 'venro:placement-select', event => this.openGroup(this.groups.find(group => group.dataset.customizeGroup === (event.detail.id === 'photo' ? 'photo' : 'text'))));
        on(this.root, 'venro:design-render', () => { this.sync(); update(); });
        for (const type of ['input', 'change']) on(this.form, type, () => this.sync());
        for (const type of ['scroll', 'resize']) {
          on(window, type, update);
          on(window.visualViewport, type, update);
        }
        on(this.form, 'focusin', event => {
          update();
          if (event.target.tagName !== 'SUMMARY') requestAnimationFrame(() => this.revealFocusedField());
        });
        if (this.root.localName !== 'venro-customizer') {
        this.querySelectorAll('[data-preview-open]').forEach(button => on(button, 'click', () => this.root.querySelector('[data-enlarge]').click()));
        on(this.querySelector('[data-review-form]'), 'click', () => {
          const target = this.root.querySelector('[data-confirm]');
          target.focus();
          target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
        }
        if (this.root.matches('venro-coaster-customizer,venro-wheel-customizer')) for (const [selector, direction] of [['[data-scene-prev]', -1], ['[data-scene-next]', 1]]) {
          on(this.root.querySelector(selector), 'click', () => {
            const buttons = [...this.root.querySelectorAll('[data-scene]')];
            const index = buttons.findIndex(button => button.dataset.scene === this.root.scene);
            buttons[(index + direction + buttons.length) % buttons.length]?.click();
          });
        }
        this.sync();
        this.updateFloating();
      });
    }
    disconnectedCallback() {
      this.abort?.abort();
      cancelAnimationFrame(this.frame);
      this.bound = false;
    }
    openGroup(group) {
      if (!group) return;
      this.root.querySelector('venro-field-controls')?.close?.();
      this.groups.forEach(other => { other.open = other === group; });
    }
    sync() {
      if (!this.labels || !this.groups) return;
      const r = this.root, holiday = r.localName === 'venro-holiday-customizer';
      const label = field => field.closest('.vn-field')?.querySelector(':scope > span')?.childNodes[0]?.textContent?.trim() || '';
      const usable = field => !field.disabled && !field.closest('[hidden]');
      const product = this.groups.find(group => group.dataset.customizeGroup === 'product');
      const options = [...product.querySelectorAll('select')].filter(usable).map(field => field.selectedOptions[0]?.textContent.trim()).filter(Boolean);
      const background = product.querySelector('[data-background],[data-background-color]');
      if (background && usable(background)) options.push(label(background) + ': ' + background.value.toUpperCase());
      const textFields = [...this.form.querySelectorAll('[data-top-text],[data-center-text],[data-bottom-text],[data-badge-left],[data-badge-right],[data-text]')].filter(usable);
      const texts = textFields.filter(field => field.value.trim());
      const words = texts.map(field => field.value.trim());
      const font = r.querySelector('[data-font],[data-font-select]');
      let textDetail = texts.map(field => (textFields.length > 1 ? label(field) + ': ' : '') + field.value.trim()).join(' · ');
      if (textDetail && font) textDetail += ' · ' + font.selectedOptions[0].textContent.trim();
      const photo = holiday ? r.active?.image && r.active.file : r.photo && r.querySelector('[data-photo]')?.files[0];
      const values = {
        product: { caption: options.join(' · '), detail: options.join(' · ') },
        text: { caption: words.join(' · ') || this.labels.empty_text, detail: textDetail || this.labels.empty_text },
        photo: { caption: photo ? this.labels.photo_added : this.labels.empty_photo, detail: photo?.name || this.labels.empty_photo }
      };
      if (holiday && r.config) {
        const states = [...r.states.values()];
        const areaLabel = s => states.length > 1 ? r.label(s.area.label) + ': ' : '';
        const ornament = r.catalog || r.closest('.vh-ornament');
        values.product.detail = [r.label(r.config.material), ornament && r.displaySize ? r.displaySize(r.config.size) : r.label(r.config.size), r.label(r.config.style), ...states.filter(s => !s.transparent).map(s => areaLabel(s) + s.background.toUpperCase())].join(' · ');
        values.text.caption = states.filter(s => s.text.trim()).map(s => areaLabel(s) + s.text.trim()).join(' · ') || this.labels.empty_text;
        values.photo.caption = states.some(s => s.image && s.file) ? this.labels.photo_added : this.labels.empty_photo;
        values.text.detail = states.filter(s => s.text.trim()).map(s => areaLabel(s) + s.text.trim() + ' · ' + s.font).join(' / ') || this.labels.empty_text;
        values.photo.detail = states.filter(s => s.image && s.file).map(s => areaLabel(s) + s.file.name).join(' / ') || this.labels.empty_photo;
      }
      const key = JSON.stringify(values);
      if (key === this.summaryKey) return;
      this.summaryKey = key;
      const list = document.createElement('dl');
      list.className = 'vn-design-summary';
      this.groups.forEach(group => {
        if (group.hidden) return;
        const name = group.dataset.customizeGroup, value = values[name];
        group.querySelector('.vn-customize-caption').textContent = value.caption;
        const row = document.createElement('div'), term = document.createElement('dt'), detail = document.createElement('dd');
        term.textContent = this.labels[name]; detail.textContent = value.detail;
        row.append(term, detail); list.append(row);
      });
      r.querySelector('[data-order-summary]').replaceChildren(list);
    }
    drawFloating() {
      const source = this.root.localName === 'venro-customizer' ? this.root.canvas : this.root.display;
      if (!source?.width || !source?.height) return;
      const ctx = this.canvas.getContext('2d'), w = this.canvas.width, h = this.canvas.height;
      const scale = Math.min(w / source.width, h / source.height);
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = '#f2f1eb'; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(source, (w-source.width*scale)/2, (h-source.height*scale)/2, source.width*scale, source.height*scale);
    }
    revealFocusedField() {
      if (!matchMedia('(max-width:989px)').matches || !this.form.contains(document.activeElement) || document.activeElement.tagName === 'SUMMARY') return;
      const viewport = window.visualViewport, offset = viewport?.offsetTop || 0;
      const rect = document.activeElement.getBoundingClientRect();
      const top = this.floating.hidden ? offset + 16 : this.floating.getBoundingClientRect().bottom + 16;
      const bottom = offset + (viewport?.height || innerHeight) - (this.querySelector('.vn-mobile-studio-bar')?.offsetHeight || 0) - 16;
      const shift = rect.top < top || rect.bottom > bottom ? rect.top - top - Math.max(0,(bottom-top-rect.height)/2) : 0;
      if (shift) window.scrollBy({ top: shift, behavior: 'instant' });
    }
    updateFloating() {
      if (!this.form || !this.isConnected) return;
      const viewport = window.visualViewport, offset = viewport?.offsetTop || 0, height = viewport?.height || innerHeight;
      const stage = this.root.querySelector('.vn-preview-stage').getBoundingClientRect(), form = this.form.getBoundingClientRect();
      const visible = matchMedia('(max-width:989px)').matches && stage.bottom <= offset + 8 && form.bottom > offset + 180 && form.top < offset + height;
      this.root.classList.toggle('vn-compact-viewport', height < 480);
      this.floating.style.top = offset + 'px';
      this.floating.style.setProperty('--vn-visible-height', height + 'px');
      this.floating.hidden = !visible;
      this.root.classList.toggle('vn-has-floating-preview', visible);
      this.root.style.setProperty('--vn-floating-height', this.floating.offsetHeight + 'px');
      if (visible) {
        this.drawFloating();
        if (height !== this.lastHeight) this.revealFocusedField();
      }
      this.lastHeight = height;
    }
  }
  if (!customElements.get('venro-product-controls')) customElements.define('venro-product-controls', VenroProductControls);
})();
